/*
 *
 * Copyright (c) Vaisala Oyj. All rights reserved.
 *
 */

import * as React from 'react';
import * as yup from 'yup';
import { Button, Form, Heading, InputField, Label, NotificationType, Size, Login as VaiLogin } from '@vaisala/rockhopper-components';
import pkg from '../../package.json';
import styles from './LoginPage.module.css';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuth } from '../state/auth/AuthContext';
import { useNotification } from '../state/notifications';

type LoginValues = {
  username: string;
  password: string;
};

yup.setLocale({
  mixed: {
    required: { key: 'validation.field_required' },
  },
  string: {
    min: ({ min }: { min: number }) => ({ key: 'validation.field_too_short', value: min }),
  },
});

const schema = yup.object().shape({
  username: yup.string().required().min(1),
  password: yup.string().required().min(8),
});

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const { handleSubmit, register, watch, formState: { errors } } = useForm<LoginValues>({ resolver: yupResolver(schema) });
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const { login, status, responseMessage } = useAuth();
  const navigate = useNavigate();
  const { notify } = useNotification();

  const username = watch('username');
  const password = watch('password');

  const onSubmit = React.useCallback(
    async (loginValues: LoginValues) => {
      setIsLoading(true);
      await login(loginValues.username, loginValues.password);
      setIsLoading(false);
    },
    [login]
  );

  React.useEffect(() => {
    if (status === 'INACTIVE' && responseMessage) {
      setErrorMessage(responseMessage);
    } else {
      setErrorMessage(null);
    }
  }, [status, responseMessage]);

  const handleForgotPassword = () => {
    notify({
      type: NotificationType.Default,
      content: "Contact the support: helpdesk@vaisala.com",
      duration: 5000})
  };

  return (
    <VaiLogin
      copyright={t('login.copyright')}
      productName={t('app.name')}
      error={errorMessage}
      privacyPolicyText="Privacy policy"
      privacyPolicyUrl="https://www.vaisala.com/en/vaisala-policies"
      productVersion={`Version ${pkg.version}`}
    >
      <Form preventDefault={true} onSubmit={handleSubmit(onSubmit)}>
        <Heading level="2" className="vai-margin-top-none">
          {t('login.title')}
        </Heading>
        <Label className="vai-padding-right-none">
          {t('login.username')}
          <InputField width={Size.Container} autoComplete="username" disabled={isLoading} {...register('username')} />
        </Label>
        <Label className="vai-padding-right-none">
          {t('login.password')}
          <InputField type="password" width={Size.Container} autoComplete="current-password" disabled={isLoading} {...register('password')} />
        </Label>
        <div className={styles.loginButtons + ' vai-margin-top-m'}>
          <Button onClick={handleForgotPassword}>
            {t('login.forgot_password')}
          </Button>
          <Button disabled={isLoading || !username || !password} type={'submit'}>
            {t('login.submit')}
          </Button>
        </div>
      </Form>
    </VaiLogin>
  );
};

export default LoginPage;


